import { createContext, useContext } from 'react';

export interface PageTitleContextValue {
  /**
   * Set a dynamic page title that overrides the route `handle` title for the
   * current route (e.g. an entity name once it loads). Pass `null` to clear and
   * fall back to the handle title. Reset automatically on route change.
   */
  setTitle: (title: string | null) => void;
  /**
   * True when an `AdminLayoutRoute` shell is already rendering the chrome above
   * this point. A nested `<AdminLayout>` reads this via `useShellRendered()` and
   * renders only its children — so pages that still self-wrap in `<AdminLayout>`
   * during the migration don't double the sidebar/topbar.
   */
  shellRendered: boolean;
}

const noop = () => {};

export const PageTitleContext = createContext<PageTitleContextValue | null>(null);

/**
 * Pages call this to set a dynamic title. No-ops safely when used outside an
 * `AdminLayoutRoute` (e.g. in unit tests that render a page directly).
 */
export function usePageTitle(): (title: string | null) => void {
  return useContext(PageTitleContext)?.setTitle ?? noop;
}

/**
 * Internal helper for `AdminLayout`'s nested-shell guard: whether an
 * `AdminLayoutRoute` already renders the chrome above this component.
 */
export function useShellRendered(): boolean {
  return useContext(PageTitleContext)?.shellRendered ?? false;
}
